import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFpQaEngagements } from '../../hooks/useFpQaEngagements';
import { setFpQaHandoff } from '@/shared/utils/fpQaHandoff';
import { trackFpQaEvent } from '../../utils/fpQaRum';
import { ClientPicker } from './ClientPicker';
import { SavedEngagementsList } from './SavedEngagementsList';
import { ResultsPage } from './ResultsPage';

type QATabView =
  | { kind: 'landing' }
  | { kind: 'client-picker' }
  | { kind: 'results'; engagementId: string };

const ENGAGEMENT_QUERY_PARAM = 'fp_qa_engagement';

export const QATab = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<QATabView>({ kind: 'landing' });
  const { data: engagements = [], isLoading } = useFpQaEngagements();
  const tabViewFiredRef = useRef(false);

  // Tab view RUM — fire once per mount.
  useEffect(() => {
    if (!tabViewFiredRef.current) {
      tabViewFiredRef.current = true;
      trackFpQaEvent('fp_qa_tab_view');
    }
  }, []);

  // On mount and whenever the URL param changes, deep-link into the results
  // page for the engagement returned by the chatbot handoff.
  useEffect(() => {
    const engagementId = searchParams.get(ENGAGEMENT_QUERY_PARAM);
    if (engagementId) {
      setView({ kind: 'results', engagementId });
    }
  }, [searchParams]);

  const closeResults = () => {
    if (searchParams.has(ENGAGEMENT_QUERY_PARAM)) {
      const next = new URLSearchParams(searchParams);
      next.delete(ENGAGEMENT_QUERY_PARAM);
      setSearchParams(next, { replace: true });
    }
    setView({ kind: 'landing' });
  };

  const startQAFlow = (selection: { clientUserId: string | null; name: string | null }) => {
    setFpQaHandoff(selection);
    // Tell the chatbot to drop any prior conversation in localStorage and
    // start fresh. Without this, ChatInterface would resume the FP's most
    // recent care-seeker chat history.
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('force_new_chat', 'true');
      // Also clear the persisted chat session id directly — belt and
      // suspenders, since force_new_chat is consumed once on mount.
      window.localStorage.removeItem('chat_session_id');
    }
    trackFpQaEvent('fp_qa_started', { clientUserId: selection.clientUserId });
    navigate('/care-navigator');
  };

  if (view.kind === 'results') {
    return (
      <ResultsPage engagementId={view.engagementId} onBack={closeResults} />
    );
  }

  if (view.kind === 'client-picker') {
    return (
      <ClientPicker
        onCancel={() => setView({ kind: 'landing' })}
        onConfirm={startQAFlow}
      />
    );
  }

  return (
    <SavedEngagementsList
      engagements={engagements}
      isLoading={isLoading}
      onStartNew={() => setView({ kind: 'client-picker' })}
      onOpenEngagement={(id) => {
        trackFpQaEvent('fp_qa_engagement_reopened', { engagementId: id });
        setView({ kind: 'results', engagementId: id });
      }}
    />
  );
};
